/** @file Constrained Delaunay triangles of a mesh's hull and interior points (autorig's triangulation). */
import { ringArea } from '@pose2d/autorig/mesh/ringArea';
import { triangulate } from '@pose2d/autorig/mesh/triangulate';

/** Triangles in the caller's vertex indices, and the interior vertices the triangulation could not place. */
export interface Retriangulated {
  triangles: number[];
  skipped: number[];
}

/**
 * Triangulates rest positions `points` (2 per vertex, y-up) with vertices 0..hull−1 as the outline; a clockwise outline
 * is mirrored first (winding does not matter to the renderer). null when the outline crosses itself or has no area.
 */
export function retriangulate(points: readonly number[], hull: number): Retriangulated | null {
  const p = ringArea(points.slice(0, 2 * hull)) > 0 ? points : points.map((v, i) => (i % 2 === 1 ? -v : v));
  try {
    const { triangles, kept } = triangulate(p, hull);
    const out = triangles.map((t) => kept[t] ?? -1);
    if (out.length < 3 || out.some((v) => v < 0)) return null;
    const used = new Set(kept);
    return { triangles: out, skipped: Array.from({ length: points.length / 2 }, (_, v) => v).filter((v) => !used.has(v)) };
  } catch {
    return null;
  }
}
